import api from "./api";
import { AUTH_ENDPOINTS } from "../config/api";

// TypeScript interfaces for authentication responses
interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface AuthTokenResponse extends AuthResponse {
  access_token?: string;
}

interface PhoneAvailabilityResponse extends AuthResponse {
  available: boolean;
}

interface VerificationStatusData {
  email_verified: boolean;
  phone_verified: boolean;
  can_access_dashboard: boolean;
}

interface VerificationStatusResponse extends AuthResponse {
  data?: VerificationStatusData;
}

// Utility function to extract error message from error response
const getErrorMessage = (error: any): string => {
  console.error("Error response:", error.response?.data);

  if (error.response?.data) {
    const data = error.response.data;

    // If data is a validation error array
    if (Array.isArray(data)) {
      const messages = data.map((err: any) => {
        if (err.msg) return err.msg;
        if (err.message) return err.message;
        return String(err);
      });
      return messages.join(", ");
    }

    // If data is an object with validation errors
    if (data.detail && Array.isArray(data.detail)) {
      const messages = data.detail.map((err: any) => {
        if (err.msg) return err.msg;
        if (err.message) return err.message;
        return String(err);
      });
      return messages.join(", ");
    }

    // If data has a simple detail message
    if (data.detail && typeof data.detail === "string") {
      return data.detail;
    }

    // If data has a simple message
    if (data.message && typeof data.message === "string") {
      return data.message;
    }

    // If data is a simple string
    if (typeof data === "string") {
      return data;
    }

    // If we have a validation error object
    if (data.type === "validation_error") {
      return String(data.msg || "Validation error");
    }
  }

  return error.message || "An error occurred";
};

// Email-based login - sends OTP to email
export const requestLoginOTP = async (
  email: string,
  turnstileToken?: string
) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.OTP_LOGIN, {
      email,
      token: turnstileToken,
    });

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error("Login OTP request error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Email signup function
export const requestSignup = async (userData: {
  fullname: string;
  email: string;
  company: string;
  turnstileToken?: string;
}) => {
  try {
    const requestData = {
      fullname: userData.fullname,
      email: userData.email,
      company: userData.company,
      token: userData.turnstileToken,
    };
    
    const response = await api.post(AUTH_ENDPOINTS.SIGNUP, requestData);
    return {
      success: true,
      message: response.data.message || "Verification email sent successfully",
    };
  } catch (error: any) {
    console.error("Email signup error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Old signup function - maintained for backward compatibility
export const requestSignupOTP = async (userData: {
  fullname: string;
  email: string;
  phone?: string;
  company: string;
  turnstileToken?: string;
}) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.SIGNUP, userData);
    return {
      success: true,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error("Signup error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Use requestSignup instead
export const requestEmailSignup = async (userData: {
  fullname: string;
  email: string;
  company: string;
  turnstileToken?: string;
}) => {
  return requestSignup(userData);
};

// Token verification cache to prevent duplicate verification attempts
const tokenVerificationCache = new Map<string, {
  result: any;
  timestamp: number;
}>();

const TOKEN_CACHE_DURATION = 60000; // 1 minute cache for tokens

// Verify email with token
export const verifyEmailToken = async (token: string) => {
  try {
    // Check if we've already verified this token recently
    const cached = tokenVerificationCache.get(token);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < TOKEN_CACHE_DURATION) {
      console.log("Using cached token verification result");
      return cached.result;
    }
    
    const response = await api.post(AUTH_ENDPOINTS.EMAIL_VERIFY, { token });
    
    const result = {
      success: true,
      message: response.data.message || "Email verified successfully",
      data: response.data,
    };
    
    // Cache the result to prevent duplicate requests
    tokenVerificationCache.set(token, {
      result,
      timestamp: now,
    });
    
    // Clear verification status cache since email verification status changed
    clearVerificationStatusCache();
    
    return result;
  } catch (error: any) {
    console.error("Email verification error:", error);
    
    // Handle 410 Gone (already verified) as a success case
    if (error.response?.status === 410) {
      const result = {
        success: true,
        message: "Email is already verified",
        data: {
          already_verified: true,
          email_verified: true,
          phone_verified: false,
          can_access_dashboard: false,
        },
      };
      
      // Cache this result too
      tokenVerificationCache.set(token, {
        result,
        timestamp: Date.now(),
      });
      
      return result;
    }
    
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Use requestLoginOTP instead
export const requestEmailLogin = async (email: string, turnstileToken?: string) => {
  return requestLoginOTP(email, turnstileToken);
};

// Verify email OTP for login
export const verifyEmailOTP = async (email: string, otp: string) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.VERIFY_OTP, {
      email,
      otp,
    });

    // Store token if received
    if (response.data.access_token) {
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("userType", "lead");
      localStorage.setItem("tokenTimestamp", Date.now().toString());
      localStorage.setItem("email", email);
      // Preserve phone if it exists
      const existingPhone = localStorage.getItem("phone");
      if (existingPhone) {
        localStorage.setItem("phone", existingPhone);
      }
      
      // Clear verification status cache since user data changed
      clearVerificationStatusCache();
    }

    return {
      success: true,
      message: response.data.message,
      access_token: response.data.access_token,
    };
  } catch (error: any) {
    console.error("Email OTP verification error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Cache for verification status to reduce API calls
const verificationStatusCache = new Map<string, {
  data: VerificationStatusData;
  timestamp: number;
}>();

const CACHE_DURATION = 5000; // 5 seconds cache

// Get verification status
export const getVerificationStatus = async (email?: string, phone?: string): Promise<VerificationStatusResponse> => {
  try {
    // Create cache key
    const cacheKey = `${email || 'no-email'}-${phone || 'no-phone'}`;
    const now = Date.now();
    
    // Check cache first
    const cached = verificationStatusCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log("Using cached verification status");
      return {
        success: true,
        data: cached.data,
      };
    }
    
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (phone) params.append('phone', phone);
    
    const response = await api.get(`${AUTH_ENDPOINTS.VERIFICATION_STATUS}?${params.toString()}`);
    
    const result = {
      success: true,
      data: response.data,
    };
    
    // Cache the result
    verificationStatusCache.set(cacheKey, {
      data: response.data,
      timestamp: now,
    });
    
    return result;
  } catch (error: any) {
    console.error("Verification status error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Clear verification status cache (call this when user data changes)
export const clearVerificationStatusCache = () => {
  verificationStatusCache.clear();
};

// Resend email verification
export const resendEmailVerification = async (email: string) => {
  try {
    // Use the signup endpoint with resend flag to resend verification
    const response = await api.post(AUTH_ENDPOINTS.SIGNUP, { 
      email,
      fullname: "", // Required field, but not used for resend
      company: "", // Required field, but not used for resend
      resend: true 
    });
    return {
      success: true,
      message: response.data.message || "Verification email sent successfully",
    };
  } catch (error: any) {
    console.error("Resend email verification error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Send phone verification OTP for new users
export const sendPhoneVerificationOTP = async (phone: string, email?: string): Promise<AuthResponse> => {
  try {
    // Get email from localStorage if not provided
    const userEmail = email || localStorage.getItem("email");
    
    if (!userEmail) {
      return {
        success: false,
        error: "Email is required for phone verification. Please complete email verification first.",
      };
    }

    const response = await api.post(AUTH_ENDPOINTS.PHONE_SEND_OTP, {
      phone,
      email: userEmail,
    });
    
    return {
      success: true,
      message: response.data.message || "OTP sent to your phone number",
    };
  } catch (error: any) {
    console.error("Phone OTP request error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Verify phone number with OTP for new users
export const verifyPhoneOTP = async (phone: string, otp: string, email?: string): Promise<AuthTokenResponse> => {
  try {
    // Get email from localStorage if not provided
    const userEmail = email || localStorage.getItem("email");
    
    if (!userEmail) {
      return {
        success: false,
        error: "Email is required for phone verification. Please complete email verification first.",
      };
    }

    const response = await api.post(AUTH_ENDPOINTS.PHONE_VERIFY, {
      phone,
      otp,
      email: userEmail,
    });

    // Store token if received
    if (response.data.access_token) {
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("userType", "lead");
      localStorage.setItem("tokenTimestamp", Date.now().toString());
      localStorage.setItem("phone", phone);
      // Preserve email if it exists
      const existingEmail = localStorage.getItem("email");
      if (existingEmail) {
        localStorage.setItem("email", existingEmail);
      }
      
      // Clear verification status cache since user data changed
      clearVerificationStatusCache();
    }

    return {
      success: true,
      message: response.data.message || "Phone verified successfully",
      access_token: response.data.access_token,
    };
  } catch (error: any) {
    console.error("Phone OTP verification error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Check if phone number is available for use
export const checkPhoneAvailability = async (phone: string): Promise<PhoneAvailabilityResponse> => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.PHONE_CHECK_AVAILABILITY, {
      phone,
    });

    return {
      success: true,
      available: response.data.available,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error("Phone availability check error:", error);
    return {
      success: false,
      available: false,
      error: getErrorMessage(error),
    };
  }
};

// Resend phone verification OTP
export const resendPhoneVerificationOTP = async (phone: string, email?: string): Promise<AuthResponse> => {
  try {
    // Get email from localStorage if not provided
    const userEmail = email || localStorage.getItem("email");
    
    if (!userEmail) {
      return {
        success: false,
        error: "Email is required for phone verification. Please complete email verification first.",
      };
    }

    const response = await api.post(AUTH_ENDPOINTS.RESEND_OTP, {
      phone,
      email: userEmail,
      purpose: "phone_verification",
    });
    
    return {
      success: true,
      message: response.data.message || "New OTP sent to your phone",
    };
  } catch (error: any) {
    console.error("Resend phone OTP error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Use verifyEmailOTP instead
export const verifySignupOTP = async (phone: string, otpCode: string) => {
  console.warn("verifySignupOTP is deprecated. Use verifyEmailOTP instead.");
  try {
    const response = await api.post(AUTH_ENDPOINTS.VERIFY_OTP, {
      phone,
      otp: otpCode,
    });

    // Store token if received
    if (response.data.access_token) {
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("userType", "lead");
      localStorage.setItem("tokenTimestamp", Date.now().toString());
      localStorage.setItem("phone", phone);
    }

    return {
      success: true,
      message: response.data.message,
      access_token: response.data.access_token,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Use verifyEmailOTP instead
export const verifyLoginOTP = async (phone: string, otpCode: string) => {
  console.warn("verifyLoginOTP is deprecated. Use verifyEmailOTP instead.");
  try {
    const response = await api.post(AUTH_ENDPOINTS.VERIFY_OTP, {
      phone,
      otp: otpCode,
    });

    const { access_token } = response.data;

    if (access_token) {
      localStorage.setItem("token", access_token);
      localStorage.setItem("userType", "lead");
      localStorage.setItem("tokenTimestamp", Date.now().toString());
      localStorage.setItem("phone", phone);
    }

    return {
      success: true,
      access_token,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Resend OTP for signup or login
export const resendSignupOTP = async (
  phone?: string,
  email?: string,
  turnstileToken?: string,
  purpose?: string
) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.RESEND_OTP, {
      phone,
      email,
      turnstileToken,
      purpose: purpose || "email_login"
    });

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Admin login
export const loginAdmin = async (username: string, password: string, turnstileToken?: string) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.ADMIN_LOGIN, {
      username: username.trim(),
      password: password.trim(),
      token: turnstileToken,
    });

    // Save tokens and user type
    localStorage.setItem("token", response.data.access_token);
    localStorage.setItem("adminToken", response.data.access_token);
    localStorage.setItem("userType", "admin");
    // Set token timestamp
    localStorage.setItem("tokenTimestamp", Date.now().toString());

    return {
      success: true,
    };
  } catch (error: any) {
    console.error("Login error:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// DEPRECATED: Old OTP login function - kept for backward compatibility
export const loginWithOTP = async (otpCode: string, turnstileToken: string) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.OTP_LOGIN, {
      otp: otpCode,
      turnstileToken,
    });

    const { access_token } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("userType", "lead");
    // Set token timestamp
    localStorage.setItem("tokenTimestamp", Date.now().toString());

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Check if user is logged in
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  // Check if token has expired (older than 24 hours)
  const tokenTimestamp = localStorage.getItem("tokenTimestamp");
  if (tokenTimestamp) {
    const timestamp = parseInt(tokenTimestamp, 10);
    const currentTime = Date.now();
    if (currentTime - timestamp > 86400000) {
      // Token expired, clean up and return false
      logout();
      return false;
    }
  } else if (token) {
    // If we have a token but no timestamp, set it now
    localStorage.setItem("tokenTimestamp", Date.now().toString());
  }

  return true;
};

// Logout
export const logout = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("userType");
  localStorage.removeItem("tokenTimestamp");
  localStorage.removeItem("phone");
  localStorage.removeItem("email");
};
